import React, { useState } from 'react';
import { Sparkles, Copy, Check } from 'lucide-react';
import { streamAI } from '../../utils/streamAI';

const EmailHelperTab = ({ password }) => {
  const [enquiry, setEnquiry] = useState('');
  const [instructions, setInstructions] = useState('');
  const [output, setOutput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!enquiry.trim() || busy) return;
    setBusy(true);
    setError('');
    setOutput('');
    const prompt = `Draft a reply to this customer/stockist enquiry:\n\n"${enquiry}"${instructions.trim() ? `\n\nExtra instructions from Ricky's Bees: ${instructions}` : ''}`;
    await streamAI('/api/ai/generate', { mode: 'email', prompt, password },
      (chunk) => setOutput(prev => prev + chunk),
      () => setBusy(false),
      (err) => { setError(err); setBusy(false); });
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm";

  return (
    <div data-testid="email-helper-tab">
      <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Email AI</h1>
      <p className="text-sm text-foreground/60 mb-6">Paste a customer or stockist enquiry and Claude will draft a reply in the Ricky's Bees voice.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Customer / stockist enquiry *</label>
            <textarea rows={8} value={enquiry} onChange={e => setEnquiry(e.target.value)}
              placeholder="Paste the email you received..." className={inputCls} data-testid="email-enquiry-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Extra instructions (optional)</label>
            <input value={instructions} onChange={e => setInstructions(e.target.value)}
              placeholder="e.g. offer them a sample pack, mention we restock Friday" className={inputCls} data-testid="email-instructions-input" />
          </div>
          <button onClick={generate} disabled={busy || !enquiry.trim()}
            className="inline-flex items-center space-x-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold disabled:opacity-40"
            data-testid="email-generate-button">
            <Sparkles className="w-4 h-4" /><span>{busy ? 'Drafting...' : 'Draft Reply'}</span>
          </button>
          {error && <p className="text-red-500 text-sm" data-testid="email-error">{error}</p>}
        </div>

        <div className="p-5 rounded-2xl border border-border bg-muted/30 min-h-[280px] relative">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground/50">Draft reply</p>
            {output && (
              <button onClick={copy} className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-border text-xs hover:border-primary hover:text-primary transition-colors" data-testid="email-copy-button">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}<span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            )}
          </div>
          <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" data-testid="email-output">
            {output || <span className="text-foreground/40 italic">Your drafted reply will appear here...</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailHelperTab;
